import { Stack } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import { ArcGauge } from "~/feature/event/components/MarketCalculator/components/ArcGauge";
import {
  useMarketCalculator,
  useMarketCalculatorActions,
} from "~/feature/event/providers/MarketCalculatorProvider";
import type { IMarket } from "~/types/event";

import * as Styled from "./MarketCalculatorQuestion.styles";

interface IMarketCalculatorFormProps {
  market: IMarket;
  variant?: "default" | "line";
  singleMarket?: boolean;
}

export const MarketCalculatorQuestion: FC<IMarketCalculatorFormProps> = ({
  market,
  variant = "default",
  singleMarket = false,
}) => {
  const { answer, groupedMarkets } = useMarketCalculator();
  const { setAnswer } = useMarketCalculatorActions();
  const [selectedAnswer, setSelectedAnswer] = useState<boolean>(answer);

  const handleSelectYes = useCallback(() => setSelectedAnswer(true), []);
  const handleSelectNo = useCallback(() => setSelectedAnswer(false), []);

  useEffect(() => {
    setAnswer(selectedAnswer);
  }, [selectedAnswer, setAnswer]);

  const avgChancePercent = useMemo(() => {
    const markets = singleMarket
      ? [market]
      : groupedMarkets.length > 0
        ? groupedMarkets
        : [market];
    const prices = markets
      .map((m) => m.chance ?? m.bestAsk ?? m.lastTradePrice)
      .filter((p): p is number => p !== null && p !== undefined && p > 0 && p <= 1);
    if (prices.length === 0) return null;
    return Math.round((prices.reduce((sum, p) => sum + p, 0) / prices.length) * 100);
  }, [groupedMarkets, market, singleMarket]);


  const isLine = variant === "line";

  return (
    <Styled.Root $variant={variant}>
      <Styled.QuestionRow>
        <Styled.Question component="h2" variant="caption" sx={{ fontWeight: 600 }}>
          {market?.question}
        </Styled.Question>
        {avgChancePercent !== null && (
          <ArcGauge
            percentage={answer ? avgChancePercent : 100 - Math.round(avgChancePercent)}
            size={isLine ? "compact" : "default"}
          />
        )}
      </Styled.QuestionRow>
      <Stack
        sx={{
          flexDirection: "row",
          gap: 1
        }}>
        <Styled.AnswerButton
          color="success"
          variant="outlined"
          fullWidth
          selected={answer}
          $line={isLine}
          onClick={handleSelectYes}
        >
          Yes
        </Styled.AnswerButton>
        <Styled.AnswerButton
          color="error"
          variant="outlined"
          fullWidth
          selected={!answer}
          $line={isLine}
          onClick={handleSelectNo}
        >
          No
        </Styled.AnswerButton>
      </Stack>
    </Styled.Root>
  );
};
